import React from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/ui-components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/util-components/dropdown-menu";
import {
  Copy,
  ClipboardPaste,
  CopyPlus,
  Trash2,
  BringToFront,
  SendToBack,
  ArrowUp,
  ArrowDown,
  AlignCenterHorizontal,
  AlignCenterVertical,
  Move,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Layers
} from 'lucide-react';
import { useTemplateStore, useSelectedElementId } from '../hooks/useTemplateStore';
import { useTemplatePermissions } from '../hooks/useTemplatePermissions';
import type { Element } from '@/page-components/post-generator/core/template.schemas';

// --- Shared Content Component ---

interface MenuComponents {
  Item: React.ComponentType<any>;
  Separator: React.ComponentType<any>;
  Sub: React.ComponentType<any>;
  SubTrigger: React.ComponentType<any>;
  SubContent: React.ComponentType<any>;
  Label: React.ComponentType<any>;
  Group: React.ComponentType<any>;
  Shortcut: React.ComponentType<any>;
}

interface ElementMenuContentProps {
  element: Element | null;
  targetId: string | null;
  clipboard: Element | null;
  components: MenuComponents;
  actions: {
    copyElement: () => void;
    pasteElement: () => void;
    duplicateElement: () => void;
    deleteElement: (id: string) => void;
    moveElementUp: (id: string) => void;
    moveElementDown: (id: string) => void;
    moveElementToTop: (id: string) => void;
    moveElementToBottom: (id: string) => void;
    centerElement: (id: string, axis: 'horizontal' | 'vertical' | 'both') => void;
    toggleElementVisibility: (id: string) => void;
    updateElement: (id: string, updates: Partial<Element>) => void;
  };
  permissions: {
    canDelete: boolean;
    canCopy: boolean;
    canPaste: boolean;
    canDuplicate: boolean;
  };
}

const ElementMenuContentRender: React.FC<ElementMenuContentProps> = ({
  element,
  targetId,
  clipboard,
  components: C,
  actions,
  permissions
}) => {
  if (!element) {
    // Canvas Background Context
    return (
      <>
        <C.Item onClick={actions.pasteElement} disabled={!clipboard || !permissions.canPaste}>
          <ClipboardPaste className="mr-2 h-4 w-4" />
          Einfügen
          <C.Shortcut>⌘V</C.Shortcut>
        </C.Item>
      </>
    );
  }

  return (
    <>
      <C.Label className="text-xs text-muted-foreground font-normal">
        {element.name || 'Element'}
      </C.Label>
      <C.Separator />
      
      {/* Editing Actions */}
      <C.Group>
        <C.Item onClick={actions.copyElement} disabled={!permissions.canCopy}>
          <Copy className="mr-2 h-4 w-4" />
          Kopieren
          <C.Shortcut>⌘C</C.Shortcut>
        </C.Item>
        <C.Item onClick={actions.pasteElement} disabled={!clipboard || !permissions.canPaste}>
          <ClipboardPaste className="mr-2 h-4 w-4" />
          Einfügen
          <C.Shortcut>⌘V</C.Shortcut>
        </C.Item>
        <C.Item onClick={actions.duplicateElement} disabled={!permissions.canDuplicate}>
          <CopyPlus className="mr-2 h-4 w-4" />
          Duplizieren
          <C.Shortcut>⌘D</C.Shortcut>
        </C.Item>
        {permissions.canDelete && (
          <C.Item 
            onClick={() => actions.deleteElement(targetId!)}
            className="text-red-600 focus:text-red-600 focus:bg-red-50"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Löschen
            <C.Shortcut>⌫</C.Shortcut>
          </C.Item>
        )}
      </C.Group>
      
      <C.Separator />
      
      {/* Layering Actions */}
      <C.Sub>
        <C.SubTrigger>
          <Layers className="mr-2 h-4 w-4" />
          Anordnung
        </C.SubTrigger>
        <C.SubContent>
          <C.Item onClick={() => actions.moveElementToTop(targetId!)}>
            <BringToFront className="mr-2 h-4 w-4" />
            In den Vordergrund
            <C.Shortcut>⌘⇧↑</C.Shortcut>
          </C.Item>
          <C.Item onClick={() => actions.moveElementUp(targetId!)}>
            <ArrowUp className="mr-2 h-4 w-4" />
            Ebene nach oben
            <C.Shortcut>⌘↑</C.Shortcut>
          </C.Item>
          <C.Item onClick={() => actions.moveElementDown(targetId!)}>
            <ArrowDown className="mr-2 h-4 w-4" />
            Ebene nach unten
            <C.Shortcut>⌘↓</C.Shortcut>
          </C.Item>
          <C.Item onClick={() => actions.moveElementToBottom(targetId!)}>
            <SendToBack className="mr-2 h-4 w-4" />
            In den Hintergrund
            <C.Shortcut>⌘⇧↓</C.Shortcut>
          </C.Item>
        </C.SubContent>
      </C.Sub>
      
      {/* Alignment Actions */}
      <C.Sub>
        <C.SubTrigger>
          <Move className="mr-2 h-4 w-4" />
          Ausrichten
        </C.SubTrigger>
        <C.SubContent>
          <C.Item onClick={() => actions.centerElement(targetId!, 'horizontal')}>
            <AlignCenterHorizontal className="mr-2 h-4 w-4" />
            Horizontal zentrieren
          </C.Item>
          <C.Item onClick={() => actions.centerElement(targetId!, 'vertical')}>
            <AlignCenterVertical className="mr-2 h-4 w-4" />
            Vertikal zentrieren
          </C.Item>
          <C.Item onClick={() => actions.centerElement(targetId!, 'both')}>
            <Move className="mr-2 h-4 w-4" />
            Mitte
          </C.Item>
        </C.SubContent>
      </C.Sub>
      
      <C.Separator />
      
      {/* Properties Actions */}
      <C.Group>
        <C.Item onClick={() => actions.toggleElementVisibility(targetId!)}>
          {element.visible ? (
            <>
              <EyeOff className="mr-2 h-4 w-4" />
              Verstecken
            </>
          ) : (
            <>
              <Eye className="mr-2 h-4 w-4" />
              Anzeigen
            </>
          )}
        </C.Item>
        <C.Item onClick={() => actions.updateElement(targetId!, { locked: !element.locked })}>
          {element.locked ? (
            <>
              <Unlock className="mr-2 h-4 w-4" />
              Entsperren
            </>
          ) : (
            <>
              <Lock className="mr-2 h-4 w-4" />
              Sperren
            </>
          )}
        </C.Item>
      </C.Group>
    </>
  );
};

// --- Context Menu Wrapper ---

interface ElementContextMenuProps {
  children: React.ReactNode;
  className?: string;
  /** Optional: Force a specific element ID for the context (e.g. for Layers Panel items) */
  elementId?: string;
}

export const ElementContextMenu: React.FC<ElementContextMenuProps> = ({ 
  children, 
  className,
  elementId: propElementId 
}) => {
  const { canEditTemplate: canManageElements, canAddWatermarks, canEditWatermarks } = useTemplatePermissions();

  const selectedElementId = useSelectedElementId();
  const template = useTemplateStore(s => s.template);
  
  // Actions
  const copyElement = useTemplateStore(s => s.copyElement);
  const pasteElement = useTemplateStore(s => s.pasteElement);
  const duplicateElement = useTemplateStore(s => s.duplicateElement);
  const deleteElement = useTemplateStore(s => s.deleteElement);
  
  const moveElementUp = useTemplateStore(s => s.moveElementUp);
  const moveElementDown = useTemplateStore(s => s.moveElementDown);
  const moveElementToTop = useTemplateStore(s => s.moveElementToTop);
  const moveElementToBottom = useTemplateStore(s => s.moveElementToBottom);
  
  const centerElement = useTemplateStore(s => s.centerElement);
  
  const toggleElementVisibility = useTemplateStore(s => s.toggleElementVisibility);
  const updateElement = useTemplateStore(s => s.updateElement);
  const selectElement = useTemplateStore(s => s.selectElement);

  const targetId = propElementId || selectedElementId;
  const element = targetId ? template.elements.find(el => el.id === targetId) : null;
  const clipboard = useTemplateStore(s => s.clipboard);

  const handleSelect = () => {
    if (propElementId) {
      selectElement(propElementId);
    }
  };

  const contextComponents: MenuComponents = {
    Item: ContextMenuItem,
    Separator: ContextMenuSeparator,
    Sub: ContextMenuSub,
    SubTrigger: ContextMenuSubTrigger,
    SubContent: ContextMenuSubContent,
    Label: ContextMenuLabel,
    Group: ContextMenuGroup,
    Shortcut: ContextMenuShortcut,
  };

  const actions = {
    copyElement,
    pasteElement,
    duplicateElement,
    deleteElement,
    moveElementUp,
    moveElementDown,
    moveElementToTop,
    moveElementToBottom,
    centerElement,
    toggleElementVisibility,
    updateElement,
  };

  const permissions = {
    canDelete: canManageElements && (!element?.isWatermark || canEditWatermarks),
    canCopy: canManageElements,
    canPaste: canManageElements && (!clipboard?.isWatermark || canAddWatermarks),
    canDuplicate: canManageElements && (!element?.isWatermark || canAddWatermarks),
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger className={className} onContextMenu={handleSelect}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-64">
        <ElementMenuContentRender
          element={element || null}
          targetId={targetId}
          clipboard={clipboard}
          components={contextComponents}
          actions={actions}
          permissions={permissions}
        />
      </ContextMenuContent>
    </ContextMenu>
  );
};

// --- Dropdown Menu Wrapper (for 3-dots button) ---

interface ElementDropdownMenuProps {
  elementId: string;
  children: React.ReactNode;
}

export const ElementDropdownMenu: React.FC<ElementDropdownMenuProps> = ({ 
  elementId, 
  children 
}) => {
  const { canEditTemplate, canAddWatermarks, canEditWatermarks } = useTemplatePermissions();

  const template = useTemplateStore(s => s.template);
  
  // Actions
  const copyElement = useTemplateStore(s => s.copyElement);
  const pasteElement = useTemplateStore(s => s.pasteElement);
  const duplicateElement = useTemplateStore(s => s.duplicateElement);
  const deleteElement = useTemplateStore(s => s.deleteElement);
  
  const moveElementUp = useTemplateStore(s => s.moveElementUp);
  const moveElementDown = useTemplateStore(s => s.moveElementDown);
  const moveElementToTop = useTemplateStore(s => s.moveElementToTop);
  const moveElementToBottom = useTemplateStore(s => s.moveElementToBottom);
  
  const centerElement = useTemplateStore(s => s.centerElement);
  
  const toggleElementVisibility = useTemplateStore(s => s.toggleElementVisibility);
  const updateElement = useTemplateStore(s => s.updateElement);
  const selectElement = useTemplateStore(s => s.selectElement);

  const element = template.elements.find(el => el.id === elementId) || null;
  const clipboard = useTemplateStore(s => s.clipboard);

  const dropdownComponents: MenuComponents = {
    Item: DropdownMenuItem,
    Separator: DropdownMenuSeparator,
    Sub: DropdownMenuSub,
    SubTrigger: DropdownMenuSubTrigger,
    SubContent: DropdownMenuSubContent,
    Label: DropdownMenuLabel,
    Group: DropdownMenuGroup,
    Shortcut: ({ children, className }: any) => <span className={`ml-auto text-xs tracking-widest opacity-60 ${className}`}>{children}</span>,
  };

  const actions = {
    copyElement,
    pasteElement,
    duplicateElement,
    deleteElement,
    moveElementUp,
    moveElementDown,
    moveElementToTop,
    moveElementToBottom,
    centerElement,
    toggleElementVisibility,
    updateElement,
  };

  const permissions = {
    canDelete: canEditTemplate && (!element?.isWatermark || canEditWatermarks),
    canCopy: canEditTemplate,
    canPaste: canEditTemplate && (!clipboard?.isWatermark || canAddWatermarks),
    canDuplicate: canEditTemplate && (!element?.isWatermark || canAddWatermarks),
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => {
        e.stopPropagation();
        selectElement(elementId);
      }}>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end">
        <ElementMenuContentRender
          element={element}
          targetId={elementId}
          clipboard={clipboard}
          components={dropdownComponents}
          actions={actions}
          permissions={permissions}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
